import { useState, useEffect } from "react";
import axiosInstance from "@/config/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, User, Mail, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GeneralSettings() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    login_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/settings/general`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFormData({
        login_name: response.data.login_name,
        email: response.data.email,
        password: "",
        confirm_password: "",
      });
    } catch (error) {
      toast.error("Failed to fetch settings");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password && formData.password !== formData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const updateData = {
        login_name: formData.login_name,
        email: formData.email,
      };
      
      if (formData.password) {
        updateData.password = formData.password;
      }

      await axiosInstance.put(`${API}/settings/general`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Update local storage
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      user.login_name = formData.login_name;
      user.email = formData.email;
      localStorage.setItem("user", JSON.stringify(user));
      
      toast.success("Settings updated successfully");
      setFormData({ ...formData, password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="general-settings-page">
      <div className="max-w-3xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">General Settings</h1>
          <p className="text-slate-600">Manage your account information and login credentials</p>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
            <CardDescription>Update your personal details and password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="login_name" className="flex items-center gap-2 text-slate-700">
                  <User className="w-4 h-4" />
                  Login Name
                </Label>
                <Input
                  id="login_name"
                  value={formData.login_name}
                  onChange={(e) => setFormData({ ...formData, login_name: e.target.value })}
                  placeholder="Enter your name"
                  className="h-11 border-slate-300"
                  required
                  data-testid="login-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2 text-slate-700">
                  <Mail className="w-4 h-4" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter your email"
                  className="h-11 border-slate-300"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="border-t border-slate-200 pt-6 mt-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Change Password
                </h3>
                <p className="text-sm text-slate-600 mb-4">Leave blank to keep current password</p>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-700">
                      New Password
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Enter new password"
                      className="h-11 border-slate-300"
                      data-testid="password-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm_password" className="text-slate-700">
                      Confirm New Password
                    </Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={formData.confirm_password}
                      onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                      placeholder="Confirm new password"
                      className="h-11 border-slate-300"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold px-6"
                  data-testid="save-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}